/**
 * deliveryCheckHtmlExport.js — a Conferência de Entrega em .html AUTOSSUFICIENTE.
 *
 * Captura o DOM renderizado do contêiner da Conferência (sem a sidebar/topbar do
 * shell) com o modo exportação ligado (`ExportacaoContext.expandirTudo`: todas as linhas
 * abertas, sem filtros) e monta um arquivo que abre offline com o MESMO visual:
 *   - CSS embutido: as regras de `document.styleSheets` que se aplicam à tela (Tailwind,
 *     sphere.css, NavaStyles) + as variáveis `--sp-*` do tema ativo + os estilos inline
 *     que já estão nos elementos;
 *   - a Inter embutida como data URI (`@font-face`); ícones SVG (Lucide) já são inline;
 *     imagens e canvas viram data URI;
 *   - o fundo do shell Sphere (cena azul + grade) refeito com as cores do tema.
 *
 * Segurança (o conteúdo vem da API): o DOM serializado já sai escapado; aqui saem
 * `<script>`, `<iframe>`, `<link>`…, todo atributo `on*`, `javascript:` e qualquer URL
 * externa em `src`/`href`/`url(` (link vira texto, com o endereço no `title`). Controles
 * sem sentido offline saem; os marcados `data-export="estatico"` viram texto com o mesmo
 * visual. Nenhuma requisição externa ao abrir o arquivo.
 */
import interUrl from '@/assets/fonts/InterVariable.woff2?url';
import { C, themeCssVars } from '@/theme/palette';
import { dataHoraPtBR } from './deliveryCheckExport';

// ── util ──────────────────────────────────────────────────────────────────────

/** Escape de texto para HTML (título e cabeçalho do arquivo). */
export function escHtml(s) {
  return String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

const URL_LOCAL = /^\s*(data:|#)/i;

/** Troca todo `url(...)` que não seja data URI/âncora por `none` (nada externo no CSS). */
export function semUrlExterna(css) {
  return String(css ?? '').replace(/url\(\s*(['"]?)([^'")]*)\1\s*\)/gi,
    (m, _q, alvo) => (URL_LOCAL.test(alvo) ? m : 'none'));
}

/** Lê a Inter local e devolve um data URI woff2 (null se não der — o arquivo usa a fonte do sistema). */
export async function carregarFonteInter(url = interUrl) {
  try {
    const resp = await fetch(url);
    if (!resp.ok) return null;
    const blob = await resp.blob();
    const dataUri = await new Promise((resolve, reject) => {
      const fr = new FileReader();
      fr.onload = () => resolve(String(fr.result));
      fr.onerror = () => reject(fr.error);
      fr.readAsDataURL(blob);
    });
    return dataUri.replace(/^data:[^;,]*/, 'data:font/woff2');
  } catch {
    return null;
  }
}

// ── CSS ───────────────────────────────────────────────────────────────────────

/** Separa uma lista de seletores pelas vírgulas de topo (respeita parênteses). */
function separarSeletores(lista) {
  const out = [];
  let prof = 0;
  let atual = '';
  for (const ch of String(lista)) {
    if (ch === '(') prof += 1;
    if (ch === ')') prof -= 1;
    if (ch === ',' && prof === 0) { out.push(atual); atual = ''; } else atual += ch;
  }
  out.push(atual);
  return out.map((s) => s.trim()).filter(Boolean);
}

// Pseudo-classes de estado/interação e pseudo-elementos: saem antes de testar o seletor.
const PSEUDO_DINAMICO = /::?(?:-webkit-|-moz-|-ms-)?(?:hover|focus-visible|focus-within|focus|active|visited|link|any-link|placeholder-shown|placeholder|checked|disabled|enabled|autofill|target|indeterminate|default|read-only|read-write|required|optional|invalid|valid|user-invalid|open|popover-open|modal|backdrop|before|after|first-letter|first-line|selection|marker|file-selector-button|[a-z-]*scrollbar[a-z-]*|inner-spin-button|outer-spin-button|search-[a-z-]+|datetime-[a-z-]+|date-and-time-value|calendar-picker-indicator|details-marker|textfield-decoration-container|meter-[a-z-]+|progress-[a-z-]+|slider-[a-z-]+)(?:\([^)]*\))?/gi;

/** O seletor casa com algum elemento da tela capturada (ou com html/body/shell)? */
function seAplica(seletores, raiz, extras) {
  for (const bruto of separarSeletores(seletores)) {
    const sel = bruto.replace(PSEUDO_DINAMICO, '').replace(/:is\(\s*\)|:where\(\s*\)/g, '').trim()
      || '*';
    try {
      if (raiz.matches(sel) || raiz.querySelector(sel)) return true;
      if (extras.some((el) => el && el.matches(sel))) return true;
    } catch {
      /* seletor que o navegador não entende aqui: não se aplica */
    }
  }
  return false;
}

function regraParaCss(regra, raiz, extras) {
  const tipo = regra.constructor?.name || '';
  // @font-face e @import saem: a Inter vai embutida; nada externo.
  if (tipo === 'CSSFontFaceRule' || tipo === 'CSSImportRule' || regra.type === 5 || regra.type === 3) return '';
  if (regra.selectorText !== undefined && !regra.cssRules?.length) {
    return seAplica(regra.selectorText, raiz, extras) ? regra.cssText : '';
  }
  if (regra.cssRules && regra.cssRules.length !== undefined && !(tipo === 'CSSKeyframesRule' || regra.type === 7)) {
    const dentro = [...regra.cssRules].map((r) => regraParaCss(r, raiz, extras)).filter(Boolean).join('\n');
    if (!dentro) return '';
    if (regra.selectorText !== undefined) {
      // regra de estilo com aninhamento: mantém só se o seletor de fora se aplica
      return seAplica(regra.selectorText, raiz, extras) ? regra.cssText : '';
    }
    if (tipo === 'CSSMediaRule' || regra.type === 4) return `@media ${regra.conditionText ?? regra.media?.mediaText ?? 'all'} {\n${dentro}\n}`;
    if (tipo === 'CSSSupportsRule' || regra.type === 12) return `@supports ${regra.conditionText} {\n${dentro}\n}`;
    if (tipo === 'CSSLayerBlockRule') return `@layer${regra.name ? ` ${regra.name}` : ''} {\n${dentro}\n}`;
    if (tipo === 'CSSContainerRule') return `@container ${regra.conditionText} {\n${dentro}\n}`;
    return '';
  }
  return regra.cssText || '';   // @keyframes, @property, @layer a, b; …
}

/**
 * As regras de `document.styleSheets` que se aplicam à tela capturada, sem `@font-face`,
 * `@import` e sem URL externa. Folhas de outra origem (não legíveis) são ignoradas.
 */
export function coletarCss(raiz, doc = document) {
  const extras = [doc.documentElement, doc.body, raiz.closest?.('.sp-app')].filter(Boolean);
  const partes = [];
  for (const folha of [...(doc.styleSheets ?? [])]) {
    let regras;
    try {
      regras = folha.cssRules;
    } catch {
      continue;
    }
    if (!regras) continue;
    for (const r of [...regras]) {
      const css = regraParaCss(r, raiz, extras);
      if (css) partes.push(css);
    }
  }
  return semUrlExterna(partes.join('\n'));
}

// ── DOM ───────────────────────────────────────────────────────────────────────

const REMOVER = 'script, noscript, iframe, frame, frameset, object, embed, applet, link, meta, base, template, portal';
const PROPS_DE_BOTAO = ['display', 'font-size', 'font-weight', 'line-height', 'text-align', 'padding',
  'border', 'border-radius', 'background-color', 'color', 'align-items', 'justify-content', 'gap',
  'white-space', 'vertical-align', 'box-sizing', 'letter-spacing', 'width'];

function estiloComputado(vivo, props) {
  try {
    const cs = vivo.ownerDocument.defaultView.getComputedStyle(vivo);
    return props.map((p) => {
      const v = cs.getPropertyValue(p);
      return v ? `${p}: ${v};` : '';
    }).join(' ');
  } catch {
    return '';
  }
}

/** Troca `el` por outro elemento `tag`, mantendo filhos, atributos seguros e o estilo. */
function trocarPor(el, tag, estiloBase = '') {
  const doc = el.ownerDocument;
  const novo = doc.createElement(tag);
  for (const a of [...el.attributes]) {
    const nome = a.name.toLowerCase();
    if (['type', 'disabled', 'href', 'target', 'rel', 'value', 'name', 'form', 'formaction',
      'aria-pressed', 'aria-expanded', 'aria-haspopup', 'aria-controls', 'tabindex', 'style'].includes(nome)) continue;
    novo.setAttribute(a.name, a.value);
  }
  const estilo = `${estiloBase} ${el.getAttribute('style') || ''}`.trim();
  if (estilo) novo.setAttribute('style', estilo);
  while (el.firstChild) novo.appendChild(el.firstChild);
  el.replaceWith(novo);
  return novo;
}

function imagemComoDataUri(vivo) {
  try {
    if (String(vivo.currentSrc || vivo.src || '').startsWith('data:')) return vivo.currentSrc || vivo.src;
    const canvas = vivo.ownerDocument.createElement('canvas');
    canvas.width = vivo.naturalWidth || vivo.width;
    canvas.height = vivo.naturalHeight || vivo.height;
    const ctx = canvas.getContext('2d');
    if (!ctx || !canvas.width) return null;
    ctx.drawImage(vivo, 0, 0);
    return canvas.toDataURL('image/png');
  } catch {
    return null;   // imagem de outra origem ou ainda não carregada
  }
}

/** Atributos perigosos ou que apontam para fora saem; link externo vira texto. */
function limparAtributos(el) {
  for (const a of [...el.attributes]) {
    const nome = a.name.toLowerCase();
    const valor = String(a.value || '');
    if (nome.startsWith('on')) { el.removeAttribute(a.name); continue; }
    if (nome === 'data-export') { el.removeAttribute(a.name); continue; }
    if (nome === 'style') {
      const limpo = semUrlExterna(valor);
      if (limpo !== valor) el.setAttribute('style', limpo);
      continue;
    }
    if (['href', 'src', 'xlink:href', 'action', 'formaction', 'srcset', 'poster', 'background',
      'data', 'codebase', 'ping'].includes(nome)) {
      if (!URL_LOCAL.test(valor) || /^\s*data:\s*text\/html/i.test(valor)) el.removeAttribute(a.name);
    }
    if (/^\s*javascript:/i.test(valor) || /^\s*vbscript:/i.test(valor)) el.removeAttribute(a.name);
  }
}

/**
 * Clona o contêiner capturado e o deixa estático e seguro para o arquivo.
 * Precisa do contêiner VIVO (valores de campos, estilo computado dos botões, imagens).
 */
export function prepararClone(vivo) {
  const clone = vivo.cloneNode(true);
  const SEL = 'button, img, canvas, textarea, input, select, a';
  const vivos = [...vivo.querySelectorAll(SEL)];
  const copias = [...clone.querySelectorAll(SEL)];
  const pares = copias.map((c, i) => [c, vivos[i]]);

  // 1) o que não faz sentido offline sai
  clone.querySelectorAll('[data-export="omitir"]').forEach((el) => el.remove());
  clone.querySelectorAll(REMOVER).forEach((el) => el.remove());

  for (const [c, v] of pares) {
    if (!clone.contains(c)) continue;   // estava dentro de algo removido
    const tag = c.tagName.toLowerCase();
    if (tag === 'button') {
      if (c.getAttribute('data-export') === 'estatico') {
        trocarPor(c, 'span', `${estiloComputado(v, PROPS_DE_BOTAO)} cursor: default;`);
      } else {
        c.remove();
      }
    } else if (tag === 'a') {
      const href = c.getAttribute('href') || '';
      if (!/^\s*#/.test(href)) {
        const novo = trocarPor(c, 'span', estiloComputado(v, ['color', 'text-decoration']));
        if (/^https?:\/\//i.test(href)) novo.setAttribute('title', href);
      }
    } else if (tag === 'img') {
      const uri = imagemComoDataUri(v);
      if (uri) { c.setAttribute('src', uri); c.removeAttribute('srcset'); } else c.remove();
    } else if (tag === 'canvas') {
      let uri = null;
      try { uri = v.toDataURL('image/png'); } catch { uri = null; }
      if (uri) {
        const img = trocarPor(c, 'img');
        img.setAttribute('src', uri);
      } else c.remove();
    } else if (tag === 'textarea') {
      const texto = String(v?.value ?? '').trim();
      if (texto) {
        const div = trocarPor(c, 'div', 'white-space: pre-wrap;');
        div.textContent = texto;
      } else c.remove();
    } else if (tag === 'select') {
      const span = trocarPor(c, 'span');
      span.textContent = v?.selectedOptions?.[0]?.textContent ?? '';
    } else if (tag === 'input') {
      const tipo = (c.getAttribute('type') || 'text').toLowerCase();
      if (tipo === 'checkbox' || tipo === 'radio') {
        if (v?.checked) c.setAttribute('checked', ''); else c.removeAttribute('checked');
        c.setAttribute('disabled', '');
      } else if (tipo === 'hidden' || tipo === 'password' || tipo === 'file') {
        c.remove();
      } else {
        const span = trocarPor(c, 'span');
        span.textContent = v?.value ?? '';
      }
    }
  }

  // 2) tudo o que abre, aberto
  clone.querySelectorAll('details').forEach((d) => d.setAttribute('open', ''));

  // 3) atributos: sem on*, sem javascript:, sem URL externa
  limparAtributos(clone);
  clone.querySelectorAll('*').forEach(limparAtributos);
  return clone;
}

// ── arquivo ───────────────────────────────────────────────────────────────────

function varsCss(vars) {
  return Object.entries(vars).map(([k, v]) => `${k}: ${v};`).join(' ');
}

/** Recuo do contêiner dentro da área de conteúdo do shell (para manter o layout da tela). */
function recuoDaTela(vivo) {
  try {
    const area = vivo.closest('.sp-app-content');
    if (!area) return { esquerda: 24, topo: 16 };
    const a = area.getBoundingClientRect();
    const b = vivo.getBoundingClientRect();
    const esquerda = Math.max(0, Math.round(b.left - a.left));
    const topo = Math.max(0, Math.round(b.top - a.top));
    return { esquerda: esquerda || 24, topo: topo || 16 };
  } catch {
    return { esquerda: 24, topo: 16 };
  }
}

/**
 * Monta o .html autossuficiente da Conferência.
 * @param {object} p
 * @param {HTMLElement} p.container contêiner VIVO da Conferência, já com `expandirTudo`
 * @param {string} p.projeto nome do projeto
 * @param {Date} [p.geradoEm]
 * @param {string|null} [p.fonteDataUri] Inter como data URI (carregarFonteInter)
 * @param {Document} [p.doc]
 * @returns {string}
 */
export function montarHtmlDaConferencia({
  container, projeto, geradoEm = new Date(), fonteDataUri = null, doc = document,
}) {
  if (!container) throw new Error('Conferência não encontrada na tela.');
  const quando = dataHoraPtBR(geradoEm);
  const titulo = `Conferência de Entrega — ${projeto || '—'} — ${quando}`;
  const clone = prepararClone(container);
  const css = coletarCss(container, doc);
  const vars = varsCss(themeCssVars());
  const { esquerda, topo } = recuoDaTela(container);
  const fonte = fonteDataUri && /^data:/i.test(fonteDataUri)
    ? `@font-face { font-family: InterVariable; src: url(${fonteDataUri}) format('woff2'); `
      + 'font-weight: 100 900; font-style: normal; font-display: swap; }'
    : '';
  const fundo = `radial-gradient(ellipse at 68% 0%, ${C.blackElev2}E6 0%, transparent 46%), `
    + `radial-gradient(ellipse at 0% 100%, ${C.blackElev}99 0%, transparent 40%), `
    + `linear-gradient(140deg, ${C.black}, ${C.bgDeep} 72%)`;
  const grade = `linear-gradient(${C.cyberDim}1A 1px, transparent 1px), `
    + `linear-gradient(90deg, ${C.cyberDim}1A 1px, transparent 1px)`;

  const base = `
:root { ${vars} color-scheme: dark; }
html, body { margin: 0; padding: 0; height: auto; min-height: 100%; background: ${C.black}; color: ${C.neutral}; }
.sp-app.nf-export { height: auto !important; min-height: 100vh; overflow: visible !important; }
.nf-export-fundo { position: fixed; inset: 0; pointer-events: none; z-index: 0; background-image: ${fundo}; }
.nf-export-grade { position: fixed; inset: 0 0 auto 0; height: 420px; pointer-events: none; z-index: 0;
  background-image: ${grade}; background-size: 64px 64px;
  -webkit-mask-image: linear-gradient(#000a, #0000); mask-image: linear-gradient(#000a, #0000); }
.nf-export-conteudo { position: relative; z-index: 1; padding: ${topo}px ${esquerda}px 40px; }
.nf-export-cabecalho { font-size: 11.5px; letter-spacing: .02em; color: ${C.neutralMuted};
  border-bottom: 1px solid ${C.blackBorder}; padding: 12px 0 10px; margin: 0 4px 14px; max-width: 1040px; }
.nf-export-cabecalho strong { color: ${C.neutralDim}; font-weight: 600; }
@media print { .nf-export-fundo, .nf-export-grade { position: absolute; } }`;

  return `<!doctype html>
<html lang="pt-BR">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<meta name="color-scheme" content="dark">
<meta name="generator" content="Nava Sphere — Conferência de Entrega">
<title>${escHtml(titulo)}</title>
<style>${fonte}</style>
<style>
${css}
</style>
<style>${base}
</style>
</head>
<body>
<div class="sp-app nf-export" style="${escHtml(vars)}">
<div class="nf-export-fundo" aria-hidden="true"></div>
<div class="nf-export-grade" aria-hidden="true"></div>
<main class="nf-export-conteudo">
<div class="nf-export-cabecalho">Conferência de Entrega — <strong>${escHtml(projeto || '—')}</strong> — exportado em ${escHtml(quando)}</div>
${clone.outerHTML}
</main>
</div>
</body>
</html>
`;
}
